import type { ReactNode } from 'react';
import { motion } from 'framer-motion';

export interface SelectableCardOption {
  key: string;
  title: string;
  desc?: string;
  icon?: string;
  meta?: ReactNode;
  badge?: string;
  disabled?: boolean;
}

interface SelectableCardProps {
  option: SelectableCardOption;
  active: boolean;
  index?: number;
  onClick: () => void;
  variant?: 'plain' | 'icon' | 'compact';
  showCheck?: boolean;
}

export default function SelectableCard({
  option,
  active,
  index = 0,
  onClick,
  variant = option.icon ? 'icon' : 'plain',
  showCheck = true,
}: SelectableCardProps) {
  const isPlain = variant === 'plain';
  const isCompact = variant === 'compact';

  return (
    <motion.button
      key={option.key}
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.07, duration: 0.35 }}
      onClick={onClick}
      disabled={option.disabled}
      className={`w-full text-left rounded-2xl border transition-all duration-200 cursor-pointer relative disabled:opacity-50 disabled:cursor-not-allowed ${
        isPlain ? 'p-5' : isCompact ? 'p-3' : 'p-4 flex items-center gap-3.5'
      } ${
        active
          ? 'border-navy bg-white shadow-md shadow-navy/8'
          : 'border-cream-dark bg-white hover:border-beige'
      }`}
    >
      {!isPlain && option.icon && (
        <div
          className={`${isCompact ? 'w-9 h-9' : 'w-10 h-10'} rounded-xl flex items-center justify-center shrink-0 ${
            active ? 'bg-navy text-white' : 'bg-cream-light text-navy-light'
          }`}
        >
          <i className={`${option.icon} ${isCompact ? 'text-sm' : 'text-base'}`} />
        </div>
      )}

      <div className={isPlain ? 'pr-9' : 'flex-1 min-w-0'}>
        <div className="flex items-center gap-2 min-w-0">
          <p className={`${isPlain ? 'text-base font-semibold' : 'text-sm'} font-semibold text-navy truncate`}>
            {option.title}
          </p>
          {option.badge && (
            <span className="text-[9px] font-medium text-softgold bg-softgold/10 px-1.5 py-0.5 rounded-full shrink-0">
              {option.badge}
            </span>
          )}
        </div>
        {option.desc && (
          <p className={`${isPlain ? 'text-xs mt-1.5' : 'text-[11px] mt-1'} text-navy-light leading-relaxed`}>
            {option.desc}
          </p>
        )}
        {option.meta && <div className="text-softgold mt-3 text-xs">{option.meta}</div>}
      </div>

      {showCheck && active && (
        <div className={`${isPlain ? 'absolute top-4 right-4' : 'shrink-0 mt-1'} w-6 h-6 rounded-full bg-navy flex items-center justify-center`}>
          <i className="ri-check-line text-white text-xs" />
        </div>
      )}
    </motion.button>
  );
}
